import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { GoogleLogo } from "../components/GoogleLogo";
import { Spinner } from "../components/Loading";
import { useAuth } from "../lib/auth";

export function SignupPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState<"email" | "google" | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading("email");
    try {
      const message = await auth.signup(fullName, email, password);
      if (auth.accessToken) navigate("/dashboard", { replace: true });
      else navigate("/login", { replace: true, state: { notice: message } });
    } catch (submissionError) {
      setError(submissionError instanceof Error ? submissionError.message : "Signup failed");
    } finally {
      setLoading(null);
    }
  }

  async function googleSignup() {
    setError("");
    setLoading("google");
    try {
      await auth.googleLogin();
    } catch (submissionError) {
      setError(submissionError instanceof Error ? submissionError.message : "Google signup failed");
      setLoading(null);
    }
  }

  return (
    <AuthLayout subtitle="Create your LimitX account">
      <h2>Get started</h2>
      {error ? <div className="alert alert-error">{error}</div> : null}
      <button
        type="button"
        className="btn btn-google"
        onClick={googleSignup}
        disabled={Boolean(loading)}
      >
        {loading === "google" ? <Spinner small /> : <GoogleLogo />}
        {loading === "google" ? "Redirecting to Google..." : "Continue with Google"}
      </button>
      <div className="auth-divider">or</div>
      <form className="form-stack" onSubmit={submit}>
        <div className="field">
          <label htmlFor="fullName">Full name</label>
          <input
            id="fullName"
            className="input-field"
            autoComplete="name"
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="signupEmail">Email</label>
          <input
            id="signupEmail"
            className="input-field"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="signupPassword">Password</label>
          <input
            id="signupPassword"
            className="input-field"
            type="password"
            minLength={8}
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="confirmPassword">Confirm password</label>
          <input
            id="confirmPassword"
            className="input-field"
            type="password"
            minLength={8}
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            required
          />
        </div>
        <button className="btn btn-primary btn-block" disabled={Boolean(loading)}>
          {loading === "email" ? <Spinner small /> : null}
          {loading === "email" ? "Creating account..." : "Create Account"}
        </button>
      </form>
      <p className="auth-switch">
        Already have an account?{" "}
        <Link to="/login" className="text-link">
          Sign In
        </Link>
      </p>
    </AuthLayout>
  );
}
